import { CameraOutlined, PrinterOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Empty, Flex, Form, Image, Input, InputNumber, Modal, Segmented, Space, Table, Tag, Typography, Upload, type UploadFile } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { DateRangeFilter, SearchFilter, useSearchFilter } from "@/components/list/filters";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useActiveWarehouse } from "@/lib/useWarehouses";
import { printPdf } from "./packLabel";
import { parseSerials } from "./SerialImport";

type Row = Schemas["DamagedStock"];
type Disposal = Schemas["Disposal"];
type Status = Disposal["status"];
const MAX_PHOTOS = 10;
// Damaged goods older than this wait too long for a decision.
const STALE_DAYS = 30;
const STATUS_COLOR: Record<Status, string> = { requested: "gold", approved: "processing", rejected: "default", done: "success" };

/** A phone photo made small enough to keep (JPEG, 1600 px at most); the file itself when the browser cannot read it. */
async function shrinkPhoto(file: Blob): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("photo"))), "image/jpeg", 0.85));
  } catch {
    return file;
  }
}

async function uploadPhoto(disposalId: string, file: Blob) {
  const blob = await shrinkPhoto(file);
  return unwrap(
    api.POST("/v1/damage/disposals/{disposalID}/photos", {
      params: { path: { disposalID: disposalId } },
      body: blob as unknown as string,
      bodySerializer: (b: unknown) => b as Blob,
      headers: { "Content-Type": blob.type },
    }),
  );
}

function loadPhoto(disposalId: string, photoId: string) {
  return unwrap(
    api.GET("/v1/damage/disposals/{disposalID}/photos/{photoID}", { params: { path: { disposalID: disposalId, photoID: photoId } }, parseAs: "blob" }),
  ) as Promise<Blob>;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => {
    for (const k of ["damage", "inventory", "finance"]) void queryClient.invalidateQueries({ queryKey: [k] });
  };
}

/**
 * Gudang > Barang rusak (Plan §6.22): damaged stock waiting for a decision,
 * and the disposals: asked by the warehouse (the units are held), approved
 * by finance, then done at the warehouse with photos (the berita acara).
 */
export function Damaged() {
  const { t } = useI18n();
  const { current, loading } = useActiveWarehouse();
  const [tab, setTab] = useUrlFilter<"stock" | "history" | "sources">("tab", "stock", ["stock", "history", "sources"]);
  if (!loading && !current) return <Empty description={t("damage.noWarehouse")} style={{ marginTop: 48 }} />;
  return (
    <>
      <Flex align="center" wrap gap={16} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("damage.title")}
        </Typography.Title>
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: "stock", label: t("damage.tab.stock") },
            { value: "history", label: t("damage.tab.history") },
            { value: "sources", label: t("damage.tab.sources") },
          ]}
        />
        {current && <Typography.Text type="secondary">{current.name}</Typography.Text>}
      </Flex>
      {current &&
        (tab === "stock" ? (
          <DamagedStock warehouseId={current.id} wms={current.wmsEnabled} />
        ) : tab === "history" ? (
          <Disposals warehouseId={current.id} wms={current.wmsEnabled} />
        ) : (
          <Sources warehouseId={current.id} />
        ))}
    </>
  );
}

function DamagedStock({ warehouseId, wms }: { warehouseId: string; wms: boolean }) {
  const { t, formatNumber, formatMoney } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const search = useSearchFilter();
  const [selected, setSelected] = useState<string[]>([]);
  const [disposing, setDisposing] = useState<Row[] | null>(null);
  const [restoring, setRestoring] = useState<Row | null>(null);
  const ask = can("inventory.view", "warehouse.operate");
  const decide = can("inventory.manage", "warehouse.manage");
  const toSupplierAllowed = can("procurement.manage");
  const finance = can("finance.view");
  const list = useQuery({
    queryKey: ["damage", warehouseId, search.query],
    queryFn: () => unwrap(api.GET("/v1/damage", { params: { query: { warehouseId, ...search.query } } })),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const free = (r: Row) => r.quantity - r.held;
  const picked = rows.filter((r) => selected.includes(r.skuId) && free(r) > 0);
  const units = rows.reduce((n, r) => n + r.quantity, 0);
  const value = rows.reduce((n, r) => n + (r.value ?? 0), 0);
  const toSupplier = (rs: Row[]) => navigate(`/app/procurement/returns/new?lines=${rs.map((r) => `${r.skuId}:${free(r)}`).join(",")}`);

  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("damage.help")} />
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("damage.search")} />
        <Typography.Text type="secondary">
          {t("damage.total", { n: formatNumber(units) })}
          {finance && value > 0 ? ` · ${formatMoney(value)}` : ""}
        </Typography.Text>
        <div style={{ flex: 1 }} />
        {ask && (
          <Button danger disabled={picked.length === 0} onClick={() => setDisposing(picked)}>
            {t("damage.disposeN", { n: picked.length })}
          </Button>
        )}
        {toSupplierAllowed && (
          <Button disabled={picked.length === 0} onClick={() => toSupplier(picked)}>
            {t("damage.toSupplierN", { n: picked.length })}
          </Button>
        )}
      </Flex>
      <Table<Row>
        rowKey="skuId"
        loading={list.isFetching && !list.data}
        dataSource={rows}
        pagination={rows.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
        locale={{ emptyText: t("damage.empty") }}
        rowSelection={ask || toSupplierAllowed ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
        columns={[
          {
            title: t("catalog.code"),
            dataIndex: "skuCode",
            render: (v: string, r) => (
              <Space>
                <SkuThumb skuId={r.skuId} size={32} />
                <Space orientation="vertical" size={0}>
                  <span className="z-num">{v}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {r.skuName}
                  </Typography.Text>
                </Space>
              </Space>
            ),
          },
          {
            title: t("damage.qty"),
            dataIndex: "quantity",
            width: 150,
            align: "right",
            render: (n: number, r) => (
              <Space orientation="vertical" size={0} align="end">
                <span className="z-num">{formatNumber(n)}</span>
                {r.held > 0 && <Tag color="gold">{t("damage.held", { n: formatNumber(r.held) })}</Tag>}
                {r.claimOpen && <Tag color="purple">{t("damage.claimOpen")}</Tag>}
              </Space>
            ),
          },
          {
            title: t("damage.age"),
            dataIndex: "since",
            width: 120,
            render: (v: string | null) => {
              if (!v) return "—";
              const days = Math.floor((Date.now() - new Date(v).getTime()) / 86_400_000);
              return days >= STALE_DAYS ? <Tag color="warning">{t("damage.days", { n: days })}</Tag> : t("damage.days", { n: days });
            },
          },
          ...(wms
            ? [{ title: t("damage.locations"), dataIndex: "locations", width: 200, render: (l: string[]) => <span className="z-num">{l.join(", ") || "—"}</span> }]
            : []),
          ...(finance
            ? [{ title: t("damage.value"), dataIndex: "value", width: 150, align: "right" as const, render: (v?: number) => <span className="z-num">{v == null ? "—" : formatMoney(v)}</span> }]
            : []),
          {
            title: "",
            width: 280,
            render: (_: unknown, r: Row) =>
              free(r) > 0 && (
                <Space>
                  {decide && (
                    <Button size="small" onClick={() => setRestoring(r)}>
                      {t("damage.restore")}
                    </Button>
                  )}
                  {ask && (
                    <Button size="small" danger onClick={() => setDisposing([r])}>
                      {t("damage.dispose")}
                    </Button>
                  )}
                </Space>
              ),
          },
        ]}
      />
      {disposing && (
        <RequestModal
          warehouseId={warehouseId}
          rows={disposing}
          onClose={(done) => {
            setDisposing(null);
            if (done) setSelected([]);
          }}
        />
      )}
      {restoring && <RestoreModal warehouseId={warehouseId} wms={wms} row={restoring} max={free(restoring)} onClose={() => setRestoring(null)} />}
    </>
  );
}

function RestoreModal({ warehouseId, wms, row, max, onClose }: { warehouseId: string; wms: boolean; row: Row; max: number; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [qty, setQty] = useState<number | null>(max);
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/damage/restore", { body: { warehouseId, skuId: row.skuId, quantity: qty!, note: note.trim() } })),
    onSuccess: () => {
      void message.success(t(wms ? "damage.restoredWms" : "damage.restored"));
      refresh();
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open
      title={t("damage.restoreTitle", { code: row.skuCode })}
      okText={t("damage.restore")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !qty || !note.trim() }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t(wms ? "damage.restoreHelpWms" : "damage.restoreHelp")}</Typography.Text>
        <InputNumber min={1} max={max} precision={0} value={qty} onChange={setQty} suffix={`/ ${max}`} style={{ width: 160 }} />
        <Input.TextArea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("damage.restoreNote")} />
      </Space>
    </Modal>
  );
}

/** Ajukan pemusnahan: the units are held until finance decides. */
function RequestModal({ warehouseId, rows, onClose }: { warehouseId: string; rows: Row[]; onClose: (done: boolean) => void }) {
  const { t, formatMoney } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [qty, setQty] = useState<Record<string, number | null>>(Object.fromEntries(rows.map((r) => [r.skuId, r.quantity - r.held])));
  const [note, setNote] = useState("");
  const value = rows.reduce((n, r) => n + (qty[r.skuId] ?? 0) * (r.unitCost ?? 0), 0);
  const save = useMutation({
    mutationFn: () =>
      unwrap(api.POST("/v1/damage/disposals", { body: { warehouseId, note: note.trim(), lines: rows.map((r) => ({ skuId: r.skuId, quantity: qty[r.skuId]! })) } })),
    onSuccess: () => {
      void message.success(t("damage.requested"));
      refresh();
      onClose(true);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open
      width={600}
      title={t("damage.requestTitle")}
      okText={t("damage.request")}
      okButtonProps={{ danger: true, disabled: !note.trim() || rows.some((r) => !qty[r.skuId]) }}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={() => onClose(false)}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark={false}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("damage.requestHelp")} />
        {rows.some((r) => r.claimOpen) && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("damage.claimOpenWarn")} />}
        {rows.map((r) => (
          <Form.Item key={r.skuId} label={`${r.skuCode} · ${r.skuName}`} style={{ marginBottom: 12 }}>
            <InputNumber
              min={1}
              max={r.quantity - r.held}
              precision={0}
              value={qty[r.skuId]}
              onChange={(v) => setQty((x) => ({ ...x, [r.skuId]: v }))}
              suffix={`/ ${r.quantity - r.held}`}
              style={{ width: 160 }}
            />
          </Form.Item>
        ))}
        {can("finance.view") && value > 0 && <Typography.Paragraph type="secondary">{t("damage.disposeValue", { value: formatMoney(value) })}</Typography.Paragraph>}
        <Form.Item label={t("damage.reason")} required>
          <Input.TextArea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("damage.reasonPlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Musnahkan sekarang: photos of the goods being disposed of, IMEI for serial goods in WMS, then the units leave. */
function ExecuteModal({ d, wms, onClose }: { d: Disposal; wms: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [photos, setPhotos] = useState<UploadFile[]>([]);
  const [serials, setSerials] = useState<Record<string, string>>({});
  const serialLines = wms ? d.lines.filter((l) => l.tracking === "serial") : [];
  const ready =
    (d.photoIds.length > 0 || photos.length > 0) && serialLines.every((l) => parseSerials(serials[l.skuId] ?? "").length === l.quantity);
  const save = useMutation({
    mutationFn: async () => {
      for (const p of photos) await uploadPhoto(d.id, p.originFileObj as Blob);
      const body = serialLines.length ? { serials: Object.fromEntries(serialLines.map((l) => [l.skuId, parseSerials(serials[l.skuId] ?? "")])) } : {};
      return unwrap(api.POST("/v1/damage/disposals/{disposalID}/execute", { params: { path: { disposalID: d.id } }, body }));
    },
    onSuccess: () => {
      void message.success(t("damage.disposed"));
      refresh();
      onClose();
    },
    // Photos already sent stay with the disposal; a retry adds only new ones.
    onError: (err) => {
      setPhotos([]);
      refresh();
      void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open
      width={640}
      title={t("damage.executeTitle")}
      okText={t("damage.execute")}
      okButtonProps={{ danger: true, disabled: !ready }}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark={false}>
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("damage.disposeHelp")} />
        {d.lines.map((l) => (
          <Form.Item
            key={l.skuId}
            label={`${l.quantity}× ${l.skuCode} · ${l.skuName}`}
            extra={wms && l.locations.length > 0 ? t("damage.takeFrom", { locations: l.locations.join(", ") }) : undefined}
            style={{ marginBottom: 12 }}
          >
            {serialLines.includes(l) && (
              <Input.TextArea
                rows={2}
                className="z-num"
                placeholder={t("damage.serials", { n: l.quantity })}
                value={serials[l.skuId] ?? ""}
                onChange={(e) => setSerials((x) => ({ ...x, [l.skuId]: e.target.value }))}
              />
            )}
          </Form.Item>
        ))}
        <Form.Item label={t("damage.photos")} required extra={t("damage.photosHelp")}>
          <Upload
            listType="picture-card"
            accept="image/*"
            multiple
            maxCount={MAX_PHOTOS - d.photoIds.length}
            fileList={photos}
            beforeUpload={() => false}
            onChange={({ fileList }) => setPhotos(fileList.slice(0, MAX_PHOTOS - d.photoIds.length))}
          >
            {photos.length + d.photoIds.length < MAX_PHOTOS && <CameraOutlined style={{ fontSize: 20 }} />}
          </Upload>
          {d.photoIds.length > 0 && <Typography.Text type="secondary">{t("damage.photosHave", { n: d.photoIds.length })}</Typography.Text>}
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** A photo of a disposal, fetched with the session (not a public URL). */
function DisposalPhoto({ disposalId, photoId }: { disposalId: string; photoId: string }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let live = true;
    let made: string | undefined;
    void loadPhoto(disposalId, photoId).then(
      (b) => {
        made = URL.createObjectURL(b);
        if (live) setUrl(made);
      },
      () => undefined,
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [disposalId, photoId]);
  return <Image src={url} width={48} height={48} style={{ objectFit: "cover", borderRadius: 4, background: "var(--ant-color-fill-tertiary)" }} />;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

function dataUrl(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(b);
  });
}

function Disposals({ warehouseId, wms }: { warehouseId: string; wms: boolean }) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const tz = me?.workspace?.timezone;
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [status, setStatus] = useUrlFilter<"" | Status>("status", "", ["", "requested", "approved", "done", "rejected"]);
  const [executing, setExecuting] = useState<Disposal | null>(null);
  const approver = can("finance.manage");
  const operate = can("inventory.manage", "warehouse.operate");
  const finance = can("finance.view");
  const filters = { warehouseId, ...(status ? { status } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["damage", "disposals", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/damage/disposals", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const approve = useMutation({
    mutationFn: (d: Disposal) => unwrap(api.POST("/v1/damage/disposals/{disposalID}/approve", { params: { path: { disposalID: d.id } }, body: {} })),
    onSuccess: () => {
      void message.success(t("damage.approved"));
      refresh();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const reject = (d: Disposal) => {
    let note = "";
    modal.confirm({
      title: t("damage.rejectTitle"),
      content: <Input.TextArea rows={2} maxLength={500} placeholder={t("damage.rejectReason")} onChange={(e) => (note = e.target.value)} />,
      okText: t("damage.reject"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () =>
        unwrap(api.POST("/v1/damage/disposals/{disposalID}/reject", { params: { path: { disposalID: d.id } }, body: { note: note.trim() } })).then(refresh, (err: unknown) => {
          void message.error(errorText(err));
          throw err;
        }),
    });
  };
  const addPhoto = (d: Disposal) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void uploadPhoto(d.id, file).then(refresh, (err: unknown) => void message.error(errorText(err)));
    };
    input.click();
  };
  // Berita acara pemusnahan: printed from the browser, photos included.
  const print = async (d: Disposal) => {
    const photos = await Promise.all(d.photoIds.map((id) => loadPhoto(d.id, id).then(dataUrl, () => "")));
    const money = (v?: number) => (finance && v != null ? formatMoney(v) : "");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(t("damage.ba.title"))}</title><style>
      body{font:12px/1.5 sans-serif;margin:24px;color:#000} h1{font-size:18px;text-align:center;margin:0 0 4px} .c{text-align:center;margin:0 0 16px}
      table{width:100%;border-collapse:collapse;margin:12px 0} th,td{border:1px solid #444;padding:4px 6px;text-align:left} td.n{text-align:right}
      .sign{display:flex;justify-content:space-between;margin-top:40px} .sign div{width:30%;text-align:center} .sign span{display:block;margin-top:56px;border-top:1px solid #000}
      img{width:31%;margin:1%;object-fit:cover;aspect-ratio:4/3}</style></head><body>
      <h1>${esc(t("damage.ba.title"))}</h1><p class="c">${esc(me?.workspace?.name ?? "")} · ${esc(d.warehouseName)}</p>
      <p>${esc(t("damage.ba.intro", { date: d.doneAt ? formatDateTime(d.doneAt, tz) : "" }))}</p>
      <table><tr><th>${esc(t("catalog.code"))}</th><th>${esc(t("common.name"))}</th><th>${esc(t("damage.qty"))}</th>${finance ? `<th>${esc(t("damage.value"))}</th>` : ""}</tr>
      ${d.lines.map((l) => `<tr><td>${esc(l.skuCode)}</td><td>${esc(l.skuName)}</td><td class="n">${l.quantity}</td>${finance ? `<td class="n">${money(l.value)}</td>` : ""}</tr>`).join("")}
      <tr><th colspan="2">${esc(t("damage.ba.total"))}</th><th class="n">${d.units}</th>${finance ? `<th class="n">${money(d.value)}</th>` : ""}</tr></table>
      <p><b>${esc(t("damage.reason"))}:</b> ${esc(d.note)}</p>
      <div>${photos.filter(Boolean).map((u) => `<img src="${u}">`).join("")}</div>
      <div class="sign"><div>${esc(t("damage.ba.asked"))}<span>${esc(d.actorName)}</span></div><div>${esc(t("damage.ba.approved"))}<span>${esc(d.decidedName)}</span></div><div>${esc(t("damage.ba.done"))}<span>${esc(d.doneName)}</span></div></div>
      </body></html>`;
    printPdf(new Blob([html], { type: "text/html" }));
  };
  return (
    <>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={status}
          onChange={setStatus}
          options={[
            { value: "", label: t("damage.status.all") },
            ...(["requested", "approved", "done", "rejected"] as const).map((s) => ({ value: s, label: t(`damage.status.${s}`) })),
          ]}
        />
        <SearchFilter filter={search} placeholder={t("damage.historySearch")} />
        <DateRangeFilter range={range} />
      </Flex>
      <Table<Disposal>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("damage.historyEmpty") }}
        scroll={{ x: 1000 }}
        columns={[
          {
            title: t("damage.at"),
            dataIndex: "at",
            width: 170,
            render: (v: string, d) => (
              <Space orientation="vertical" size={2}>
                {formatDateTime(v, tz)}
                <Tag color={STATUS_COLOR[d.status]}>{t(`damage.status.${d.status}`)}</Tag>
              </Space>
            ),
          },
          {
            title: t("damage.goods"),
            render: (_, d) => (
              <Space orientation="vertical" size={0}>
                {d.lines.map((l) => (
                  <span key={l.skuId} className="z-num">
                    {l.quantity}× {l.skuCode}
                  </span>
                ))}
              </Space>
            ),
          },
          ...(finance
            ? [{ title: t("damage.value"), dataIndex: "value", width: 140, align: "right" as const, render: (v?: number) => <span className="z-num">{v == null ? "—" : formatMoney(v)}</span> }]
            : []),
          {
            title: t("damage.reason"),
            dataIndex: "note",
            render: (v: string, d) => (
              <Space orientation="vertical" size={0}>
                {v}
                {d.decisionNote && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {d.decidedName}: {d.decisionNote}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: t("damage.people"),
            width: 190,
            render: (_, d) => (
              <Space orientation="vertical" size={0} style={{ fontSize: 12 }}>
                <span>{t("damage.ba.asked")}: {d.actorName}</span>
                {d.decidedName && <span>{t(d.status === "rejected" ? "damage.rejectedBy" : "damage.ba.approved")}: {d.decidedName}</span>}
                {d.doneName && <span>{t("damage.ba.done")}: {d.doneName}</span>}
              </Space>
            ),
          },
          {
            title: t("damage.photos"),
            width: 180,
            render: (_, d) =>
              d.photoIds.length === 0 ? (
                d.status === "done" ? <Tag color="warning">{t("damage.noPhoto")}</Tag> : "—"
              ) : (
                <Image.PreviewGroup>
                  <Space size={4} wrap>
                    {d.photoIds.map((id) => (
                      <DisposalPhoto key={id} disposalId={d.id} photoId={id} />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              ),
          },
          {
            title: "",
            width: 150,
            render: (_, d) => (
              <Space orientation="vertical" size={4}>
                {d.status === "requested" && approver && (
                  <>
                    <Button size="small" type="primary" loading={approve.isPending} onClick={() => approve.mutate(d)}>
                      {t("damage.approve")}
                    </Button>
                    <Button size="small" danger onClick={() => reject(d)}>
                      {t("damage.reject")}
                    </Button>
                  </>
                )}
                {d.status === "requested" && !approver && <Typography.Text type="secondary">{t("damage.waitFinance")}</Typography.Text>}
                {d.status === "approved" && operate && (
                  <Button size="small" type="primary" danger onClick={() => setExecuting(d)}>
                    {t("damage.execute")}
                  </Button>
                )}
                {d.status === "done" && (
                  <Button size="small" icon={<PrinterOutlined />} onClick={() => void print(d)}>
                    {t("damage.ba.print")}
                  </Button>
                )}
                {d.status === "done" && operate && d.photoIds.length < MAX_PHOTOS && (
                  <Button size="small" icon={<CameraOutlined />} onClick={() => addPhoto(d)}>
                    {t("damage.addPhoto")}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      {executing && <ExecuteModal d={executing} wms={wms} onClose={() => setExecuting(null)} />}
    </>
  );
}

type Source = { source: "return" | "supplier" | "handling" | "transfer" | "other"; label: string; units: number; value?: number };

/** Sumber kerusakan: where units that became damaged came from over a period, to fix the cause. */
function Sources({ warehouseId }: { warehouseId: string }) {
  const { t, formatNumber, formatMoney } = useI18n();
  const { can } = useAuth();
  const range = useDateRange();
  const finance = can("finance.view");
  const list = useQuery({
    queryKey: ["damage", "sources", warehouseId, range.query],
    queryFn: () => unwrap(api.GET("/v1/damage/sources", { params: { query: { warehouseId, ...range.query } } })),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const total = rows.reduce((n, r) => n + r.units, 0);
  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("damage.sourcesHelp")} />
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <DateRangeFilter range={range} />
        {total > 0 && <Typography.Text type="secondary">{t("damage.total", { n: formatNumber(total) })}</Typography.Text>}
      </Flex>
      <Table<Source>
        rowKey={(r) => `${r.source}/${r.label}`}
        loading={list.isFetching && !list.data}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: t("damage.sourcesEmpty") }}
        columns={[
          { title: t("damage.source"), dataIndex: "source", width: 180, render: (v: Source["source"]) => <Tag>{t(`damage.source.${v}`)}</Tag> },
          { title: t("damage.sourceWho"), dataIndex: "label", render: (v: string) => v || "—" },
          {
            title: t("damage.qty"),
            dataIndex: "units",
            width: 160,
            align: "right",
            render: (n: number) => (
              <span className="z-num">
                {formatNumber(n)} {total > 0 && <Typography.Text type="secondary">({Math.round((n * 100) / total)}%)</Typography.Text>}
              </span>
            ),
          },
          ...(finance
            ? [{ title: t("damage.value"), dataIndex: "value", width: 160, align: "right" as const, render: (v?: number) => <span className="z-num">{v == null ? "—" : formatMoney(v)}</span> }]
            : []),
        ]}
      />
    </>
  );
}
